import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

interface UserData {
  id: string;
  badgeNumber: string;
  firstName: string;
  lastName: string;
  department: string;
  rank: string;
  role: string;
}

interface CaseItem {
  id: string;
  case_number: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  officer_name: string;
  department: string;
  date_opened: string;
  created_at: string;
  updated_at: string;
}

interface CIDRequest {
  requestType: string;
  message: string;
}

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const REQUEST_TYPES = [
  { value: 'information', label: 'Request Additional Information' },
  { value: 'collaboration', label: 'Request Collaboration / Joint Operation' },
  { value: 'evidence', label: 'Submit Evidence or Lead' },
  { value: 'urgent', label: 'Flag as Urgent / Escalate' },
  { value: 'witness', label: 'Report Witness or Suspect Info' },
  { value: 'general', label: 'General Inquiry' },
];

export default function LeoPortalPage() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState<CaseItem | null>(null);

  // Login form state
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginData, setLoginData] = useState({ badgeNumber: '', securityKey: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showLoginPw, setShowLoginPw] = useState(false);

  // Contact CID form state
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [cidRequest, setCidRequest] = useState<CIDRequest>({ requestType: 'general', message: '' });
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState('');

  useEffect(() => {
    const sessionToken = localStorage.getItem('userSession');
    const storedUserData = localStorage.getItem('userData');

    if (!sessionToken || !storedUserData) {
      // No session — show the LEO login form instead of redirecting
      setShowLoginForm(true);
      setIsLoading(false);
      return;
    }

    try {
      const user = JSON.parse(storedUserData);
      if (user.role === 'admin') {
        navigate('/admin');
        return;
      }
      if (user.role === 'user') {
        navigate('/dashboard');
        return;
      }
      setUserData(user);
    } catch {
      setShowLoginForm(true);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const handleLeoLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/leo-portal-login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            badgeNumber: loginData.badgeNumber,
            securityKey: loginData.securityKey,
          }),
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        const user = result.user;

        // Admins and full investigators go to their own portals
        if (user.role === 'admin') {
          localStorage.setItem('userSession', result.sessionToken);
          localStorage.setItem('userData', JSON.stringify(user));
          navigate('/admin');
          return;
        }
        if (user.role === 'user') {
          localStorage.setItem('userSession', result.sessionToken);
          localStorage.setItem('userData', JSON.stringify(user));
          navigate('/dashboard');
          return;
        }

        // LEO viewer — stay on this page
        localStorage.setItem('userSession', result.sessionToken);
        localStorage.setItem('userData', JSON.stringify({ ...user, role: 'leo' }));
        setUserData({ ...user, role: 'leo' });
        setShowLoginForm(false);
      } else {
        setLoginError(result.error || 'Access denied. Check your badge number and security key.');
      }
    } catch {
      setLoginError('Network error. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const fetchCases = async () => {
    setCasesLoading(true);
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('*')
        .eq('department', 'CID')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching CID cases:', error);
        return;
      }
      setCases(data || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
    } finally {
      setCasesLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchCases();
    }
  }, [userData]);

  const handleLogout = () => {
    localStorage.removeItem('userSession');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  const handleOpenRequestForm = () => {
    setCidRequest({ requestType: 'general', message: '' });
    setRequestSuccess(false);
    setRequestError('');
    setShowRequestForm(true);
  };

  const handleCloseRequestForm = () => {
    setShowRequestForm(false);
    setRequestSuccess(false);
    setRequestError('');
  };

  const handleSubmitRequest = async () => {
    if (!selectedCase || !userData) return;
    if (!cidRequest.message.trim()) {
      setRequestError('Please enter a message before submitting.');
      return;
    }
    if (cidRequest.message.trim().length < 10) {
      setRequestError('Message must be at least 10 characters.');
      return;
    }

    setRequestSubmitting(true);
    setRequestError('');

    try {
      const now = new Date().toISOString();

      const { error } = await supabase.from('cid_requests').insert({
        case_id: selectedCase.id,
        case_number: selectedCase.case_number,
        case_title: selectedCase.title,
        submitted_by_user_id: userData.id,
        submitted_by_name: `${userData.rank} ${userData.firstName} ${userData.lastName}`.trim(),
        submitted_by_badge: userData.badgeNumber,
        submitted_by_department: userData.department,
        request_type: cidRequest.requestType,
        message: cidRequest.message.trim(),
        status: 'pending',
        created_at: now,
      });

      if (error) throw error;

      // Fire Discord webhook notification (non-blocking — failure won't break the submit)
      fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/notify-leo-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            case_number: selectedCase.case_number,
            case_title: selectedCase.title,
            submitted_by_name: `${userData.rank} ${userData.firstName} ${userData.lastName}`.trim(),
            submitted_by_badge: userData.badgeNumber,
            submitted_by_department: userData.department,
            request_type: cidRequest.requestType,
            message: cidRequest.message.trim(),
            created_at: now,
          }),
        }
      ).catch(err => console.warn('Discord notification failed (non-critical):', err));

      setRequestSuccess(true);
    } catch (err) {
      console.error('Error submitting CID request:', err);
      setRequestError('Failed to submit request. Please try again.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch ((priority || '').toLowerCase()) {
      case 'high': return 'text-red-700 bg-red-50 border border-red-200';
      case 'medium': return 'text-amber-700 bg-amber-50 border border-amber-200';
      case 'low': return 'text-green-700 bg-green-50 border border-green-200';
      default: return 'text-gray-600 bg-gray-50 border border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'active':
      case 'open': return 'text-emerald-700 bg-emerald-50 border border-emerald-200';
      case 'under review': return 'text-amber-700 bg-amber-50 border border-amber-200';
      case 'closed': return 'text-gray-600 bg-gray-50 border border-gray-200';
      case 'pending': return 'text-orange-700 bg-orange-50 border border-orange-200';
      default: return 'text-gray-600 bg-gray-50 border border-gray-200';
    }
  };

  const filteredCases = cases.filter(c => {
    const matchesPriority = priorityFilter === 'all' || (c.priority || '').toLowerCase() === priorityFilter.toLowerCase();
    const matchesStatus = statusFilter === 'all' || (c.status || '').toLowerCase() === statusFilter.toLowerCase();
    const matchesSearch =
      search === '' ||
      (c.case_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.title || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.officer_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(search.toLowerCase());
    return matchesPriority && matchesStatus && matchesSearch;
  });

  const activeCases = cases.filter(c => ['active', 'open'].includes((c.status || '').toLowerCase())).length;
  const highPriority = cases.filter(c => (c.priority || '').toLowerCase() === 'high').length;
  const underReview = cases.filter(c => (c.status || '').toLowerCase() === 'under review').length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <i className="ri-loader-4-line animate-spin text-2xl text-slate-600"></i>
          <span className="text-slate-600 font-medium">Loading portal...</span>
        </div>
      </div>
    );
  }

  // ── LEO Login Screen ──
  if (showLoginForm) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col">
        {/* Top bar */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black text-white" style={{ fontFamily: 'Arial Black, sans-serif' }}>
              RMRP Federal Investigations &amp; Intelligence Portal
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">LEO Viewer Access — Read-Only Case Portal</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30">
              <i className="ri-shield-star-line mr-1"></i>
              LEO Portal
            </span>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            {/* Access info cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-emerald-900/40 border border-emerald-700/50 rounded-xl p-3">
                <p className="text-xs font-bold text-emerald-400 mb-1.5 flex items-center">
                  <i className="ri-checkbox-circle-line mr-1"></i> Your Access
                </p>
                <ul className="space-y-1">
                  {['View CID case reports', 'Search & filter cases', 'Submit flags to CID'].map(item => (
                    <li key={item} className="text-xs text-emerald-300 flex items-start space-x-1">
                      <i className="ri-check-line mt-0.5 flex-shrink-0 text-emerald-500"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-900/30 border border-red-700/40 rounded-xl p-3">
                <p className="text-xs font-bold text-red-400 mb-1.5 flex items-center">
                  <i className="ri-forbid-line mr-1"></i> Restricted
                </p>
                <ul className="space-y-1">
                  {['Create, edit, or delete any case', 'View non-CID department cases', 'Access internal notes', 'Modify case status or assignments'].map(item => (
                    <li key={item} className="text-xs text-red-300 flex items-start space-x-1">
                      <i className="ri-close-line mt-0.5 flex-shrink-0 text-red-500"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Login card */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-amber-500/10 border-b border-amber-500/20 px-6 py-4 flex items-center space-x-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                  <i className="ri-shield-star-line text-amber-400 text-xl"></i>
                </div>
                <div>
                  <h2 className="text-base font-bold text-white">LEO Viewer Sign In</h2>
                  <p className="text-xs text-slate-400">Enter your badge number &amp; the portal security key</p>
                </div>
              </div>

              <form onSubmit={handleLeoLogin} className="p-6 space-y-4">
                {/* Badge Number */}
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                    Badge / Member Number
                  </label>
                  <input
                    type="text"
                    value={loginData.badgeNumber}
                    onChange={e => setLoginData(prev => ({ ...prev, badgeNumber: e.target.value }))}
                    placeholder="Enter your community member number"
                    required
                    disabled={isLoggingIn}
                    className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">This is your member number in the community.</p>
                </div>

                {/* Security Key */}
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                    Portal Security Key
                  </label>
                  <div className="relative">
                    <input
                      type={showLoginPw ? 'text' : 'password'}
                      value={loginData.securityKey}
                      onChange={e => setLoginData(prev => ({ ...prev, securityKey: e.target.value }))}
                      placeholder="Enter the security key from Discord"
                      required
                      disabled={isLoggingIn}
                      className="w-full px-3 py-2.5 pr-10 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPw(p => !p)}
                      className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-200 cursor-pointer"
                    >
                      <i className={showLoginPw ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    <i className="ri-discord-line mr-1"></i>
                    The security key is distributed by CID administration via the Discord server.
                  </p>
                </div>

                {loginError && (
                  <div className="flex items-center space-x-2 bg-red-900/40 border border-red-700/50 rounded-lg p-3">
                    <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0"></i>
                    <p className="text-xs text-red-300">{loginError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/40 disabled:cursor-not-allowed text-slate-900 font-bold py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center space-x-2 text-sm"
                >
                  {isLoggingIn ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-login-box-line"></i>
                      <span>Access LEO Portal</span>
                    </>
                  )}
                </button>

                <div className="pt-2 border-t border-slate-700 text-center">
                  <p className="text-xs text-slate-500">
                    CID Investigator?{' '}
                    <a
                      href="/login"
                      className="text-slate-300 hover:text-white transition-colors cursor-pointer"
                    >
                      Go to Investigator Login
                    </a>
                  </p>
                </div>
              </form>
            </div>

            <p className="text-center text-xs text-slate-600 mt-4">
              <i className="ri-shield-keyhole-line mr-1"></i>
              Don&apos;t have the security key? Contact CID administration or your supervisor on Discord.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-900" style={{ fontFamily: 'Arial Black, sans-serif' }}>
              RMRP Federal Investigations &amp; Intelligence Portal
            </h1>
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-slate-500">LEO Read-Only Viewer</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                <i className="ri-eye-line mr-1"></i>
                View Only
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-900">
                {userData.rank} {userData.firstName} {userData.lastName}
              </p>
              <p className="text-xs text-slate-500">Badge #{userData.badgeNumber} · {userData.department}</p>
            </div>
            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
              <i className="ri-shield-user-line text-white text-lg"></i>
            </div>
            <button
              onClick={handleLogout}
              title="Logout"
              className="text-slate-500 hover:text-slate-800 cursor-pointer transition-colors"
            >
              <i className="ri-logout-box-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Access Notice Banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-6 py-2.5 flex items-center space-x-2">
        <i className="ri-information-line text-amber-600 text-sm"></i>
        <p className="text-sm text-amber-800">
          <strong>Restricted Access:</strong> You are viewing CID case reports in read-only mode. Use the <strong>Flag / Contact CID</strong> button on any case to send a request directly to investigators.
        </p>
      </div>

      {/* Access Level Info Card */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* What you CAN do */}
            <div className="flex items-start space-x-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-checkbox-circle-line text-emerald-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800 mb-2">Your Access Includes</p>
                <ul className="space-y-1.5">
                  {[
                    'View all active CID case reports',
                    'Search and filter cases by status or priority',
                    'Read full case details and descriptions',
                    'Submit requests or flags directly to CID investigators',
                  ].map(item => (
                    <li key={item} className="flex items-start space-x-2 text-xs text-emerald-700">
                      <i className="ri-check-line mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* What you CANNOT do */}
            <div className="flex items-start space-x-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-forbid-line text-red-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-red-800 mb-2">Restricted Actions</p>
                <ul className="space-y-1.5">
                  {[
                    'Create, edit, or delete any case',
                    'View cases from non-CID departments',
                    'Access investigator reports or internal notes',
                    'Modify case status, priority, or assignments',
                  ].map(item => (
                    <li key={item} className="flex items-start space-x-2 text-xs text-red-700">
                      <i className="ri-close-line mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-3 text-center">
            <i className="ri-shield-keyhole-line mr-1"></i>
            LEO Viewer access is granted by CID administration. Contact your supervisor if you require elevated permissions.
          </p>
        </div>
      </div>

      <main className="px-6 py-8 max-w-7xl mx-auto">
        {/* Page Title */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-1">
            <div className="w-8 h-8 bg-zinc-700 rounded flex items-center justify-center">
              <i className="ri-shield-line text-white text-sm"></i>
            </div>
            <h2 className="text-2xl font-bold text-slate-900">CID Active Cases</h2>
          </div>
          <p className="text-sm text-slate-500 ml-11">
            Criminal Investigations Division — read-only case overview
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Total CID Cases</p>
            <p className="text-3xl font-bold text-slate-900">{cases.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Active / Open</p>
            <p className="text-3xl font-bold text-emerald-600">{activeCases}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">High Priority</p>
            <p className="text-3xl font-bold text-red-600">{highPriority}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Under Review</p>
            <p className="text-3xl font-bold text-amber-600">{underReview}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer pr-8"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="open">Open</option>
              <option value="under review">Under Review</option>
              <option value="pending">Pending</option>
              <option value="closed">Closed</option>
            </select>

            {/* Priority Filter */}
            <select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 cursor-pointer pr-8"
            >
              <option value="all">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <button
              onClick={fetchCases}
              className="flex items-center space-x-1.5 px-3 py-1.5 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-refresh-line"></i>
              <span>Refresh</span>
            </button>
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="Search case number, title, officer..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 w-72"
            />
            <i className="ri-search-line absolute left-2.5 top-2.5 text-slate-400 text-sm"></i>
          </div>
        </div>

        {/* Cases Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {casesLoading ? (
            <div className="flex items-center justify-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-slate-500 mr-2"></i>
              <span className="text-slate-500">Loading CID cases...</span>
            </div>
          ) : filteredCases.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-folder-open-line text-slate-400 text-3xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-1">No cases found</h3>
              <p className="text-slate-500 text-sm">
                {search || priorityFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters or search term.'
                  : 'No CID cases have been submitted yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      {['Case Number', 'Title', 'Submitted By', 'Status', 'Priority', 'Date Opened', 'Actions'].map(h => (
                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {filteredCases.map(c => (
                      <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm font-semibold text-slate-800">{c.case_number}</span>
                        </td>
                        <td className="px-5 py-4 max-w-xs">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.title}</p>
                          {c.description && (
                            <p className="text-xs text-slate-500 truncate mt-0.5">{c.description}</p>
                          )}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-sm text-slate-700">
                          {c.officer_name || <span className="text-slate-400 italic">Unknown</span>}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getStatusColor(c.status)}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${getPriorityColor(c.priority)}`}>
                            {c.priority}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-sm text-slate-500">
                          {new Date(c.date_opened || c.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric'
                          })}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => { setSelectedCase(c); setShowRequestForm(false); setRequestSuccess(false); }}
                              className="flex items-center space-x-1 text-sm text-slate-600 hover:text-slate-900 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-eye-line"></i>
                              <span>View</span>
                            </button>
                            <span className="text-slate-300">|</span>
                            <button
                              onClick={() => { setSelectedCase(c); setShowRequestForm(true); setRequestSuccess(false); setRequestError(''); setCidRequest({ requestType: 'general', message: '' }); }}
                              className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-flag-line"></i>
                              <span>Flag CID</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
                Showing {filteredCases.length} of {cases.length} CID cases
              </div>
            </>
          )}
        </div>
      </main>

      {/* Case Detail Modal */}
      {selectedCase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-mono text-sm font-bold text-slate-500">{selectedCase.case_number}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-zinc-100 text-zinc-800 border border-zinc-200">
                      CID
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{selectedCase.title}</h3>
                </div>
                <button
                  onClick={() => { setSelectedCase(null); setShowRequestForm(false); setRequestSuccess(false); }}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer w-8 h-8 flex items-center justify-center"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Tab Toggle */}
              <div className="flex mt-4 bg-slate-100 rounded-lg p-1 w-fit">
                <button
                  onClick={() => setShowRequestForm(false)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${!showRequestForm ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <i className="ri-file-text-line mr-1.5"></i>
                  Case Details
                </button>
                <button
                  onClick={handleOpenRequestForm}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${showRequestForm ? 'bg-white text-red-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <i className="ri-flag-line mr-1.5"></i>
                  Flag / Contact CID
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {!showRequestForm ? (
                /* ── Case Details View ── */
                <div className="space-y-5">
                  <div className="flex flex-wrap gap-3">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Status</p>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedCase.status)}`}>
                        {selectedCase.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Priority</p>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getPriorityColor(selectedCase.priority)}`}>
                        {selectedCase.priority}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 bg-slate-50 rounded-xl p-4">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Submitted By</p>
                      <p className="text-sm font-medium text-slate-900">{selectedCase.officer_name || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Department</p>
                      <p className="text-sm font-medium text-slate-900">{selectedCase.department}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Date Opened</p>
                      <p className="text-sm font-medium text-slate-900">
                        {new Date(selectedCase.date_opened || selectedCase.created_at).toLocaleDateString('en-US', {
                          month: 'long', day: 'numeric', year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Last Updated</p>
                      <p className="text-sm font-medium text-slate-900">
                        {new Date(selectedCase.updated_at || selectedCase.created_at).toLocaleDateString('en-US', {
                          month: 'long', day: 'numeric', year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {selectedCase.description && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Case Description</p>
                      <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-xl p-4 border border-slate-200">
                        {selectedCase.description}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <i className="ri-lock-line text-amber-600 text-sm flex-shrink-0"></i>
                    <p className="text-xs text-amber-800">
                      Read-only access. Use the <strong>Flag / Contact CID</strong> tab to send a request to investigators.
                    </p>
                  </div>

                  <button
                    onClick={() => { setSelectedCase(null); setShowRequestForm(false); setRequestSuccess(false); }}
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Close
                  </button>
                </div>
              ) : (
                /* ── Contact CID Form ── */
                <div className="space-y-5">
                  {requestSuccess ? (
                    /* Success State */
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-emerald-600 text-3xl"></i>
                      </div>
                      <h4 className="text-lg font-bold text-slate-900 mb-2">Request Sent to CID</h4>
                      <p className="text-sm text-slate-500 mb-1">
                        Your request regarding case <span className="font-mono font-semibold text-slate-700">{selectedCase.case_number}</span> has been submitted.
                      </p>
                      <p className="text-xs text-slate-400 mb-6">A CID investigator will review your request and follow up as needed.</p>
                      <div className="flex gap-3">
                        <button
                          onClick={handleOpenRequestForm}
                          className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
                        >
                          Send Another
                        </button>
                        <button
                          onClick={() => { setSelectedCase(null); setShowRequestForm(false); setRequestSuccess(false); }}
                          className="flex-1 bg-zinc-800 hover:bg-zinc-900 text-white font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Form */
                    <>
                      {/* Case Reference */}
                      <div className="flex items-center space-x-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
                        <div className="w-8 h-8 bg-zinc-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-folder-line text-white text-sm"></i>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Regarding case</p>
                          <p className="text-sm font-semibold text-slate-900">
                            <span className="font-mono">{selectedCase.case_number}</span> — {selectedCase.title}
                          </p>
                        </div>
                      </div>

                      {/* Submitter Info */}
                      <div className="flex items-center space-x-3 bg-blue-50 border border-blue-100 rounded-xl p-3">
                        <div className="w-8 h-8 bg-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-white text-sm"></i>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Submitting as</p>
                          <p className="text-sm font-semibold text-slate-900">
                            {userData?.rank} {userData?.firstName} {userData?.lastName}
                            <span className="ml-2 text-xs font-normal text-slate-500">· Badge #{userData?.badgeNumber} · {userData?.department}</span>
                          </p>
                        </div>
                      </div>

                      {/* Request Type */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                          Request Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={cidRequest.requestType}
                          onChange={e => setCidRequest(prev => ({ ...prev, requestType: e.target.value }))}
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400 cursor-pointer bg-white"
                        >
                          {REQUEST_TYPES.map(rt => (
                            <option key={rt.value} value={rt.value}>{rt.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={cidRequest.message}
                          onChange={e => {
                            if (e.target.value.length <= 500) {
                              setCidRequest(prev => ({ ...prev, message: e.target.value }));
                            }
                          }}
                          placeholder="Describe your request, provide additional context, or share relevant information with CID investigators..."
                          rows={5}
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-400 resize-none"
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-slate-400">Minimum 10 characters</span>
                          <span className={`text-xs ${cidRequest.message.length >= 480 ? 'text-red-500' : 'text-slate-400'}`}>
                            {cidRequest.message.length}/500
                          </span>
                        </div>
                      </div>

                      {/* Error */}
                      {requestError && (
                        <div className="flex items-center space-x-2 bg-red-50 border border-red-200 rounded-xl p-3">
                          <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0"></i>
                          <p className="text-xs text-red-700">{requestError}</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-3 pt-1">
                        <button
                          onClick={handleCloseRequestForm}
                          className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSubmitRequest}
                          disabled={requestSubmitting}
                          className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center space-x-2"
                        >
                          {requestSubmitting ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <i className="ri-send-plane-line"></i>
                              <span>Send to CID</span>
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
